import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Organization, Person, ReduxState } from 'types';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { loadOrRender } from '../../utils/util/formatter';
import CardHeaderTitle from '../Widgets/Cards/CardHeaderTitle';

interface Props {
  currentOrganization: Organization;
  currentProxyPerson: Person;
  showOnlyNonDefaults?: boolean;
}

const UserAdministration: FC<Props> = ({
  showOnlyNonDefaults = false,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const [organizationDetails, setOrganizationDetails] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(null);

  const { user } = useAuthProvider();
  const userSub = user?.sub;

  useEffect(() => {
    if (props.currentOrganization?.id) {
      ConfirmAPI.getUrlWithCache(
        '/organizations/' + props.currentOrganization.id,
        '/organizations/' + props.currentOrganization.id,
        userSub,
        props.currentProxyPerson,
        {},
        (data) => {
          setOrganizationDetails(data);
        },
        (message) => {
          setErrorMessage(message);
        }
      );
    }
  }, [props.currentOrganization?.id, userSub, props.currentProxyPerson]);

  const getUserCard = useCallback(
    (label, name, placeholder, isDict = false) => {
      // @ts-expect-error
      if (showOnlyNonDefaults && !organizationDetails?.settings[name]) {
        return null;
      }
      return (
        <Card>
          <CardHeader>
            <CardHeaderTitle>{label}</CardHeaderTitle>
          </CardHeader>
          <CardBody>
            <p className="text-muted small">{placeholder}</p>
            <div className="alert alert-light small">
              <div>
                {/* @ts-expect-error */}
                {organizationDetails?.settings[name] ? (
                  isDict ? (
                    <pre>
                      {JSON.stringify(
                        // @ts-expect-error
                        organizationDetails?.settings[name],
                        null,
                        2
                      )}
                    </pre>
                  ) : (
                    // @ts-expect-error
                    organizationDetails?.settings[name].join(', ')
                  )
                ) : (
                  formatMessage({
                    id: 'app.user.organization.user_administration.cards.none',
                    defaultMessage: 'None',
                  })
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      );
    },
    // @ts-expect-error
    [showOnlyNonDefaults, organizationDetails?.settings, formatMessage]
  );

  const loadOrRenderOutput = loadOrRender(organizationDetails, errorMessage);
  if (loadOrRenderOutput) {
    return loadOrRenderOutput;
  }

  const details = [
    getUserCard(
      formatMessage({
        id: 'app.user.organization.user_administration.cards.title.system_administrators',
        defaultMessage: 'System administrators',
      }),
      'system_admins',
      formatMessage({
        id: 'app.user.organization.user_administration.cards.description.system_administrators',
        defaultMessage:
          'Typically technical admins. They have full system access to configure all aspects of Confirm for your organization.',
      })
    ),
    getUserCard(
      formatMessage({
        id: 'app.user.organization.user_administration.cards.title.hr_administrators',
        defaultMessage: 'HR administrators',
      }),
      'admins',
      formatMessage({
        id: 'app.user.organization.user_administration.cards.description.hr_administrators',
        defaultMessage:
          'Typically HRBPs i.e. HR/People admins. They have HR admin access to specific groups of people across all campaigns.',
      }),
      true
    ),
    getUserCard(
      formatMessage({
        id: 'app.user.organization.user_administration.cards.title.rating_dashboard_administrators',
        defaultMessage: 'Rating dashboard administrators',
      }),
      'rating_dashboard_admins',
      formatMessage({
        id: 'app.user.organization.user_administration.cards.description.rating_dashboard_administrators',
        defaultMessage:
          'Typically executives (and some HRBPs i.e. HR/People admins). They can see the rating dashboard of all Performance ratings.',
      })
    ),
    getUserCard(
      formatMessage({
        id: 'app.user.organization.user_administration.cards.title.engagement_dashboard_administrators',
        defaultMessage: 'Engagement dashboard administrators',
      }),
      'engagement_dashboard_admins',
      formatMessage({
        id: 'app.user.organization.user_administration.cards.description.engagement_dashboard_administrators',
        defaultMessage:
          'Typically executives (and some HRBPs i.e. HR/People admins). They can see the results of engagement surveys, along with HR administrators.',
      })
    ),
  ];

  return (
    <>
      {showOnlyNonDefaults &&
        details.filter((detail) => !!detail).length > 0 && (
          <Card>
            <h2 className="p-3 pt-4">
              <FormattedMessage
                id="app.views.organization.user_administration.customer_success_configured_settings"
                defaultMessage="Customer success configured settings"
              />
            </h2>
          </Card>
        )}
      {details.map((detail, idx) => (
        <Fragment key={idx}>{detail}</Fragment>
      ))}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization, currentProxyPerson } = state;

  return {
    currentOrganization,
    currentProxyPerson,
  };
};

export default connect(mapStateToProps)(React.memo(UserAdministration));
